import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { queries } from "@/api";
import { Chip } from "@/components/Chip";
import { ErrorNote } from "@/components/ErrorNote";
import { FlightPlayback } from "@/components/FlightPlayback";
import { ExceedanceList, LandingCard, SampleTable, TrackCharts } from "@/components/FlightRecorder";
import { FlightSystemsCard } from "@/components/FlightSystems";
import { useHandoverAt } from "@/components/FlightSteps";
import { HandoverCall } from "@/components/Handover";
import { NOT_DEFECTS, TechLogSheet } from "@/components/TechLogSheet";
import { stripTiles } from "@/lib/flightStrip";
import { PeopleAboard } from "@/components/PeopleAboard";
import { formatDateTime, formatMinutes, landingTone } from "@/lib/format";

/** The page's tabs (F477, decision 098): the one in the address, Flight by default. */
const TABS = ["flight", "log", "recorder", "numbers"] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute("/airframes/$id/flights/$fid")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: TABS.includes(search.tab as Tab) ? (search.tab as Tab) : undefined,
  }),
  component: FlightPage,
});

/**
 * One flight of the logbook (F417, decision 059; F477, decision 098): a
 * strip that answers how it went and whether anything is to be done, then
 * four tabs, Flight (the map, the playback, the charts, the limits),
 * Technical log (the sheet), Recorder (the aircraft's own figures) and
 * Numbers (one row a minute), each kind of evidence once.
 */
function FlightPage() {
  const { id, fid } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const detail = useQuery(queries.flight(id, fid));
  const airframe = useQuery(queries.airframe(id));
  const me = useQuery(queries.me());
  const log = useQuery(queries.techLog(id, fid));
  // The pilot who has the aircraft writes on its sheet (F448).
  const canWrite = !!airframe.data?.lease && !!me.data && airframe.data.lease.holder.id === me.data.user.id;
  // The handover is this pilot's to write: the last return theirs, nobody booking, nothing written since (2026-11-03).
  const meId = me.data?.user.id ?? "";
  const lastReturn = airframe.data?.ledger;
  const returnedByMe = !!airframe.data && !airframe.data.lease && !!meId && lastReturn?.last_pilot?.id === meId && !!lastReturn?.last_commit_at;
  const handoverAt = useHandoverAt(id, meId, returnedByMe);
  const handoverDue = returnedByMe && !(handoverAt !== null && new Date(handoverAt).getTime() >= new Date(lastReturn!.last_commit_at!).getTime());
  // The second of the flight shown on the map and under the charts' crosshair (F463): one for both.
  const [at, setAt] = useState<number | null>(null);
  const [focusSheet, setFocusSheet] = useState(false);
  const tab: Tab = search.tab ?? "flight";
  const show = (next: Tab) => navigate({ search: { tab: next === "flight" ? undefined : next }, replace: true });
  // "Write on the sheet" from the strip: the Technical log tab, the form in focus.
  useEffect(() => {
    if (!focusSheet || tab !== "log") return;
    const box = document.querySelector<HTMLTextAreaElement>("[data-testid='sheet-text']");
    if (box) {
      box.focus();
      setFocusSheet(false);
    }
  }, [focusSheet, tab, log.data]);
  if (detail.error) return <ErrorNote error={detail.error} />;
  if (!detail.data) return <p className="muted">Loading the flight...</p>;
  const { flight, track, analysis, systems } = detail.data;
  const t0 = track?.t[0] ?? 0;
  // The defects still open on the sheet: a warning or an inspection not closed, the maintenance and fuel lines aside.
  const openDefects = (log.data?.items ?? []).filter((e) => e.state !== "closed" && e.severity !== "note" && !NOT_DEFECTS.has(e.kind)).length;
  const tiles = stripTiles(detail.data);
  const tabs: { key: Tab; label: string; testId: string }[] = [
    { key: "flight", label: "Flight", testId: "flight-tab-flight" },
    { key: "log", label: openDefects > 0 ? `Technical log · ${openDefects} open` : "Technical log", testId: "flight-tab-log" },
    ...(systems ? [{ key: "recorder" as Tab, label: "Recorder", testId: "flight-tab-recorder" }] : []),
    ...(track ? [{ key: "numbers" as Tab, label: "Numbers", testId: "flight-tab-numbers" }] : []),
  ];
  const shown: Tab = tabs.some((t) => t.key === tab) ? tab : "flight";
  return (
    <div className="space-y-4" data-testid="flight-page">
      {handoverDue ? <HandoverCall airframeId={id} reg={airframe.data?.identity.reg} /> : null}
      <section className="card space-y-3" data-testid="flight-strip">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="text-[1.7143rem] font-bold tracking-tight" data-testid="flight-route">
            {flight.takeoff_icao || "----"} to {flight.landing_icao || "----"}
          </h1>
          {flight.landing_severity ? <Chip tone={landingTone(flight.landing_severity)}>{flight.landing_severity} landing</Chip> : null}
          {flight.has_track ? <Chip tone={flight.exceedances > 0 ? "bad" : "ok"}>{flight.exceedances === 0 ? "within the limits" : `${flight.exceedances} limit${flight.exceedances === 1 ? "" : "s"} exceeded`}</Chip> : <Chip>not recorded</Chip>}
          {openDefects > 0 ? (
            <Chip tone="warn" testId="open-defects">
              {openDefects} defect{openDefects === 1 ? "" : "s"} open
            </Chip>
          ) : null}
          {canWrite ? (
            <button
              type="button"
              className="btn btn-sm ml-auto"
              onClick={() => {
                setFocusSheet(true);
                show("log");
              }}
              data-testid="write-on-sheet"
            >
              Write on the sheet
            </button>
          ) : null}
        </header>
        <p className="muted text-[0.9286rem]">
          {formatDateTime(flight.takeoff_utc)} to {formatDateTime(flight.landing_utc)} · {formatMinutes(flight.enroute_min)} enroute · {flight.air_distance_nm} nm
        </p>
        <PeopleAboard pilot={flight.pilot} crew={flight.crew} />
        <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="flight-tiles">
          {tiles.map((t) => (
            <div key={t.label} className="rounded-lg bg-paper-2 px-3 py-2" data-testid={t.testId}>
              <dt className="kicker">{t.label}</dt>
              <dd className="tnum text-sm font-semibold">{t.value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <div className="segs" role="tablist" aria-label="The flight's evidence">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={shown === t.key} className={`seg ${shown === t.key ? "seg-active" : ""}`} onClick={() => show(t.key)} data-testid={t.testId}>
            {t.label}
          </button>
        ))}
      </div>
      {shown === "flight" ? (
        !track || !analysis ? (
          <section className="card space-y-2" data-testid="no-recording">
            <h2 className="text-[1.0714rem] font-semibold">No recording</h2>
            <p className="muted text-sm">
              The plugin recorded nothing of this flight: it was not running, is older than the recorder, or the flight comes from the airframe's history before the platform. The
              logbook keeps what the aircraft's own log said.
            </p>
          </section>
        ) : (
          <>
            <FlightPlayback track={track} exceedances={analysis.exceedances} from={flight.takeoff_icao || "----"} to={flight.landing_icao || "----"} index={at} onIndex={setAt} />
            <div className="grid gap-4 xl:grid-cols-5">
              <div className="xl:col-span-3">
                <TrackCharts track={track} hover={at} onHover={(i) => i !== null && setAt(i)} />
              </div>
              <div className="space-y-4 xl:col-span-2">
                <ExceedanceList exceedances={analysis.exceedances} t0={t0} />
                <LandingCard landing={analysis.landing} t0={t0} />
              </div>
            </div>
          </>
        )
      ) : null}
      {shown === "log" ? <TechLogSheet id={id} flightId={fid} flight={flight} reg={airframe.data?.identity.reg ?? ""} canWrite={canWrite} /> : null}
      {shown === "recorder" && systems ? <FlightSystemsCard systems={systems} /> : null}
      {shown === "numbers" && track ? <SampleTable track={track} /> : null}
    </div>
  );
}
